<template>
  <PlatformRenderer>
    <!-- START : Customer Programs Mobile -->
    <template v-slot:onMobile>
      <WCSinglePageFormContainer class="px-3">
        <WCSpinner v-if="!$configuration" size="xl" position="center" />

        <template v-else>
          <div v-for="category in sortedCustomerPrograms" :key="category.id" class="mb-4">
            <!-- START : Customer Programs Top Section -->
            <div class="wc-programs-top-section">
              <!-- START : Category Name -->
              <div class="wc-programs-top-section__left-wrap" :id="`${category.id}-group`">
                <h3>{{ category.name }}</h3>
              </div>
              <!-- END : Category Name -->
              <!-- START : Top Section Right Wrap -->
              <div
                class="wc-programs-top-section__right-wrap d-flex justify-content-center align-items-center"
              >
                <!-- START:search box -->
                <WCSearchInput
                  :ariaLabel="$t('searchProgram')"
                  :placeHolderText="$t('searchProgram')"
                  :id="`search-program ${category.name}`"
                  :value="searchText[category.name]"
                  :hasFocusInput="focusedCategoryId === category.id"
                  @focusIn="focusedCategoryId = category.id"
                  @focusOut="focusedCategoryId = null"
                  @onSearch="updateSearchText($event, category)"
                  @onClear="onClickingClear"
                  @updateValue="updateSearchText($event, category)"
                />
                <!-- END:search box -->
                <!-- START : Search Filter -->
                <!-- <WCInputGroup :class="{ 'focusable-area': focusedCategoryId === category.id }">
                  <template v-slot:left>
                    <button class="btn btn-outline text-primary" :aria-label="$t('search')">
                      <font-awesome-icon :icon="['fa', 'magnifying-glass']" />
                    </button>
                  </template>
                  <WCInput
                    :value="searchText[category.name]"
                    @input="updateSearchText($event, category)"
                    type="text"
                    class="form-control border-left-0"
                    autofocus
                    placeholder="Search Program"
                    :id="`search-program ${category.name}`"
                    @focusin="focusedCategoryId = category.id"
                    @focusout="focusedCategoryId = null"
                  />
                </WCInputGroup> -->
                <!-- END : Search Filter -->
              </div>
              <!-- END : Top Section Right Wrap -->
            </div>
            <!-- END : Customer Programs Top Section -->
            <!-- START : Customer Programs List Label -->
            <div class="mt-4 mb-3" :id="`${category.id}-select-group`">
              <span v-if="category.mutuallyExclusive">
                {{ $t('selectOneGroup') }}
              </span>
              <span v-else>{{ $t('checkTheGroups') }}</span>
            </div>
            <!-- END : Customer Programs List Label -->

            <!-- START : Customer Programs Mutually Exclusive List -->
            <div
              role="radiogroup"
              :aria-labelledby="`${category.id}-group ${category.id}-select-group`"
              v-if="category.mutuallyExclusive"
              class="wc-mobile-group-wrapper"
            >
              <WCRadioButton
                v-for="group in category.groups.slice(
                  (currentPagePerCategory[category.name] - 1) * groupsPerCategory,
                  currentPagePerCategory[category.name] * groupsPerCategory,
                )"
                :key="group.id"
                :id="group.id"
                :model="isMember(group) ? group.id : ''"
                @change="toggleGroup(group, category)"
                :name="category.name"
              >
                {{ group.name }}
              </WCRadioButton>
            </div>
            <!-- END : Customer Programs Mutually Exclusive List -->

            <!-- START : Customer Programs Non-Mutually Exclusive List -->
            <div
              v-else
              role="group"
              :aria-labelledby="`${category.id}-group ${category.id}-select-group`"
              class="wc-mobile-group-wrapper"
            >
              <WCCheckbox
                v-for="group in category.groups.slice(
                  (currentPagePerCategory[category.name] - 1) * groupsPerCategory,
                  currentPagePerCategory[category.name] * groupsPerCategory,
                )"
                :key="group.id"
                :model="isMember(group)"
                :label="group.name"
                @change="toggleGroup(group, category)"
              />
            </div>
            <!-- END : Customer Programs Non-Mutually Exclusive List -->
            <!-- START : Customer Programs Pagination -->
            <WCPagination
              v-if="category.groups.length > groupsPerCategory"
              :collectionSize="category.groups.length"
              v-model="currentPagePerCategory[category.name]"
              @input="
                currentPagePerCategory = { ...currentPagePerCategory, [category.name]: $event }
              "
              :pageSize="groupsPerCategory"
              :maxSize="3"
              :boundaryLinks="false"
              class="mt-4"
              :aria-label="`Pagination for ${category.name}`"
            />
            <!-- END : Customer Programs Pagination -->
          </div>
        </template>
      </WCSinglePageFormContainer>
    </template>
    <!-- END : Customer Programs Mobile -->
    <!-- START : Customer Programs Desktop -->
    <template v-slot:onDesktop>
      <DashboardWindow>
        <template v-slot:title>
          {{ $t('customerPrograms') }}
        </template>

        <template v-slot:body>
          <WCSpinner v-if="!$configuration" size="xl" position="center" />

          <template v-else>
            <DashboardWindowSection v-for="category in sortedCustomerPrograms" :key="category.id">
              <!-- START : Customer Programs Top Section -->
              <div class="wc-programs-top-section">
                <!-- START : Category Name -->
                <div class="wc-programs-top-section__left-wrap" :id="`${category.id}-group`">
                  <h3>{{ category.name }}</h3>
                </div>
                <!-- END : Category Name -->
                <!-- START : Top Section Right Wrap -->
                <div
                  class="wc-programs-top-section__right-wrap d-flex justify-content-center align-items-center"
                >
                  <!-- START:search box -->
                  <WCSearchInput
                    :ariaLabel="$t('searchProgram')"
                    :placeHolderText="$t('searchProgram')"
                    :id="`search-program ${category.name}`"
                    :value="searchText[category.name]"
                    :hasFocusInput="focusedCategoryId === category.id"
                    @focusIn="focusedCategoryId = category.id"
                    @focusOut="focusedCategoryId = null"
                    @onSearch="updateSearchText($event, category)"
                    @onClear="onClickingClear()"
                    @updateValue="updateSearchText($event, category)"
                  />
                  <!-- END:search box -->
                </div>
                <!-- END : Top Section Right Wrap -->
              </div>
              <!-- END : Customer Programs Top Section -->

              <!-- START : Customer Programs List Label -->
              <div class="mt-4 mb-3" :id="`${category.id}-select-group`">
                <span v-if="category.mutuallyExclusive">
                  {{ $t('selectOneGroup') }}
                </span>
                <span v-else>{{ $t('checkTheGroups') }}</span>
              </div>
              <!-- END : Customer Programs List Label -->

              <!-- START : Customer Programs Mutually Exclusive List -->
              <div
                v-if="category.mutuallyExclusive"
                role="radiogroup"
                :aria-labelledby="`${category.id}-group ${category.id}-select-group`"
                class="wc-group-wrapper"
              >
                <WCRadioButton
                  v-for="group in category.groups.slice(
                    (currentPagePerCategory[category.name] - 1) * groupsPerCategory,
                    currentPagePerCategory[category.name] * groupsPerCategory,
                  )"
                  :key="group.id"
                  :id="group.id"
                  :model="isMember(group) ? group.id : ''"
                  @change="toggleGroup(group, category)"
                  :name="category.name"
                >
                  <div class="customer-program-item">{{ group.name }}</div>
                </WCRadioButton>
              </div>
              <!-- END : Customer Programs Mutually Exclusive List -->
              <!-- START : Customer Programs Non-Mutually Exclusive List -->
              <div
                v-else
                role="group"
                :aria-labelledby="`${category.id}-group ${category.id}-select-group`"
                class="wc-group-wrapper"
              >
                <WCCheckbox
                  v-for="group in category.groups.slice(
                    (currentPagePerCategory[category.name] - 1) * groupsPerCategory,
                    currentPagePerCategory[category.name] * groupsPerCategory,
                  )"
                  :key="group.id"
                  :model="isMember(group)"
                  :label="group.name"
                  @change="toggleGroup(group, category)"
                >
                  <div class="customer-program-item">{{ group.name }}</div>
                </WCCheckbox>
              </div>
              <!-- END : Customer Programs Non-Mutually Exclusive List -->
              <!-- START : Customer Programs Pagination -->
              <WCPagination
                v-if="category.groups.length > groupsPerCategory"
                :collectionSize="category.groups.length"
                v-model="currentPagePerCategory[category.name]"
                @input="
                  currentPagePerCategory = { ...currentPagePerCategory, [category.name]: $event }
                "
                :pageSize="groupsPerCategory"
                :maxSize="6"
                class="mt-4"
                :aria-label="`Pagination for ${category.name}`"
              />
              <!-- END : Customer Programs Pagination -->
            </DashboardWindowSection>
          </template>
        </template>
      </DashboardWindow>
    </template>
    <!-- END : Customer Programs Desktop -->
  </PlatformRenderer>
</template>

<script>
import axios from 'axios';
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import WCSinglePageFormContainer from '@/modules/forms/components/WCSinglePageFormContainer/WCSinglePageFormContainer.vue';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCCheckbox from '@/modules/forms/components/WCCheckbox/WCCheckbox.vue';
import WCRadioButton from '@/modules/forms/components/WCRadioButton/WCRadioButton.vue';
import WCPagination from '@/components/WCPagination/WCPagination.vue';
import WCSearchInput from '@/components/WCSearchInput/WCSearchInput.vue';

export default {
  name: 'CustomerPrograms',
  components: {
    DashboardWindow,
    DashboardWindowSection,
    WCSpinner,
    PlatformRenderer,
    WCSinglePageFormContainer,
    WCCheckbox,
    WCRadioButton,
    WCPagination,
    WCSearchInput,
  },
  async created() {
    this.loading = true;

    try {
      const response = await axios.get('api/me/groups');
      this.memberships = response.data.data ? response.data.data : [];
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }

    this.$configuration.customerGroups.forEach(cp => {
      this.currentPagePerCategory[cp.name] = 1; // All start on 1st pagination page
      this.searchText[cp.name] = '';
    });
  },
  data() {
    return {
      memberships: [],
      loading: false,
      groupsPerCategory: 20,
      // The current page per category, needed for pagination
      currentPagePerCategory: {},
      searchText: {}, // Keys are categories and value is searchText
      focusedCategoryId: false,
    };
  },
  computed: {
    sortedCustomerPrograms() {
      return this.$configuration.customerGroups
        .map(cp => {
          const defaultSortedGroups = cp.groups ? cp.groups.sort(this.sortGroups) : [];
          const groups =
            this.searchText[cp.name] && this.searchText[cp.name].length > 0
              ? defaultSortedGroups.filter(g =>
                  g.name.toLowerCase().includes(this.searchText[cp.name].toLowerCase()),
                )
              : defaultSortedGroups;

          return {
            ...cp,
            groups,
          };
        })
        .filter(cp => cp.groups.length > 0);
    },
  },
  methods: {
    /**
     * Finds the membership record for a group if one exists
     */
    findMembership(group) {
      return this.memberships && this.memberships.length > 0
        ? this.memberships.find(membership => group.id === membership.id)
        : null;
    },
    /**
     * Determines if the current customer is a member of a group
     */
    isMember(group) {
      const membership = this.findMembership(group);
      return membership && membership.action !== 'remove';
    },
    sortGroups(lhs, rhs) {
      const lhsMember = this.isMember(lhs);
      const rhsMember = this.isMember(rhs);
      if (lhsMember && !rhsMember) {
        return -1;
      }
      if (!lhsMember && rhsMember) {
        return 1;
      }
      return 0;
    },
    /**
     * Adds or removes the customer from a group
     */
    toggleGroup(group, category) {
      let membership = this.findMembership(group);
      if (!membership) {
        membership = {
          id: group.id,
          action: 'add',
        };

        if (category.mutuallyExclusive) {
          this.removeOthers(group, category);
        }

        this.memberships.push(membership);
      } else if (membership.action === 'add') {
        if (category.mutuallyExclusive) {
          this.removeOthers(group, category);
        }

        delete membership.action;
      } else {
        membership.action = 'remove';
      }

      this.updateMemberships();
    },
    removeOthers(group, category) {
      this.memberships.forEach(m => {
        if (category.groups.find(g => g.id === group.id)) {
          // eslint-disable-next-line no-param-reassign
          m.action = 'remove';
        }
      });
    },
    /**
     * Sumbits the aggregated membership changes to the server
     */
    async updateMemberships() {
      this.$configuration.customerGroups.forEach(cp => {
        this.currentPagePerCategory[cp.name] = 1; // Reset to 1st pagination page
      });

      const changedMemberships = this.memberships.filter(m => m.action);

      try {
        const response = await axios.post('api/me/groups', changedMemberships);
        this.memberships = response.data.data ? response.data.data : [];
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
    updateSearchText(newText, category) {
      this.searchText = { ...this.searchText, [category.name]: newText };
      // Only change back to 1st pagination page if not currently on it
      if (this.currentPagePerCategory[category.name] !== 1) {
        this.currentPagePerCategory = { ...this.currentPagePerCategory, [category.name]: 1 };
      }
    },
    /**
     * on clicking clear icon, clear contents in search box
     */
    onClickingClear() {
      this.searchText = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/_tools.scss';
@import '~@/assets/styles/_settings.scss';

/*----- START : WC Group Wrapper ----- */
.wc-group-wrapper {
  display: grid;
  grid-template-rows: repeat(10, 1fr);
  grid-auto-flow: column;
  grid-auto-columns: 50% 50%;
  gap: 0.5rem;

  /*----- START : Checkbox-label & radio label ----- */
  ::v-deep .custom-control-label,
  ::v-deep .wc-radio-wrapper {
    width: 100%;
    box-sizing: border-box;
  }

  ::v-deep .wc-radio-wrapper {
    display: flex;
    align-items: center;
    label {
      margin-bottom: 0;
    }
  }

  .customer-program-item {
    margin-right: $margin-4;
    white-space: nowrap;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }
  /*----- END : Checkbox-label & radio label ----- */
}
/*----- END : WC Group Wrapper ----- */

/*----- START : WC Program Top Section ----- */
.wc-programs-top-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: $margin-2;

  @include media-breakpoint-down(xs) {
    flex-direction: column;
  }

  /*----- START : WC Program Top Right Wrap ----- */
  &__right-wrap {
    .wc-search-area {
      width: 14.5rem;
    }
    @include media-breakpoint-down(xs) {
      width: 100%;
      .wc-search-area {
        width: 100%;
      }
    }
  }
  /*----- END : WC Program Top Right Wrap ----- */
}
/*----- END : WC Program Top Section ----- */

.input-group-prepend {
  button {
    border: 1px solid var(--gray-400, $gray-400);
    border-right: 0px;
  }
}

.wc-mobile-group-wrapper {
  display: flex;
  flex-direction: column;
}
.wc-programs-top-section__right-wrap {
  .form-control:focus {
    box-shadow: none;
  }
}
</style>
